import {
  clinicianWorkbenchBasicHistoryKeys,
  clinicianWorkbenchDvtRiskFactorKeys,
  normalizePhoneInputV1,
  type ClinicianWorkbenchPatientRecordFieldPath,
  type ClinicianWorkbenchPatientRecordV1,
} from "@amplecare/contracts";

import { computeAgeYears } from "./scoring.js";

export type FieldKind =
  | "text"
  | "date"
  | "time"
  | "number"
  | "money"
  | "percent"
  | "phone"
  | "email"
  | "zip"
  | "state"
  | "boolean"
  | "select"
  | "list";

export interface ValidatableField {
  path: ClinicianWorkbenchPatientRecordFieldPath;
  kind: FieldKind;
  options?: readonly { value: string; label: string }[];
}

export interface ValidationIssue {
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath;
  severity: "error" | "warning";
  message: string;
}

export type ParsedFieldDraft =
  | { ok: true; value: unknown }
  | { ok: false; issue: ValidationIssue };

// Plausibility thresholds — deliberately named and exported so clinical
// stakeholders can tune them without spelunking through rule code.
export const plausibilityLimits = {
  heightInchesMin: 20,
  heightInchesMax: 90,
  weightPoundsMin: 2,
  weightPoundsMax: 800,
  surgeryDurationMinutesMin: 5,
  surgeryDurationMinutesMax: 600,
  ageAtServiceMaxYears: 120,
  dateOfServicePastGraceDays: 30,
  dateOfServiceFutureHorizonDays: 730,
  moneyMaxCents: 1_000_000_000,
} as const;

const usStateCodes = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP",
]);

function issue(
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
  severity: ValidationIssue["severity"],
  message: string,
): ValidationIssue {
  return { fieldPath, severity, message };
}

function error(
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
  message: string,
): ParsedFieldDraft {
  return { ok: false, issue: issue(fieldPath, "error", message) };
}

const unsignedDecimalPattern = /^(?:\d+(?:\.\d+)?|\.\d+)$/u;
const moneyDecimalPattern = /^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/u;

// Phone normalization lives in contracts (shared with the questionnaire
// transform layer) — re-exported so existing call sites keep working.
export const normalizePhoneInput = normalizePhoneInputV1;

export function normalizeDateInput(text: string): string | null {
  const trimmed = text.trim();
  let year: number;
  let month: number;
  let day: number;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(trimmed);

  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else if (mdy) {
    month = Number(mdy[1]);
    day = Number(mdy[2]);
    const rawYear = Number(mdy[3]);

    year =
      mdy[3]!.length === 2 ? (rawYear >= 30 ? 1900 + rawYear : 2000 + rawYear) : rawYear;
  } else {
    return null;
  }

  if (year < 1900 || year > 2100) {
    return null;
  }

  const candidate = new Date(Date.UTC(year, month - 1, day));

  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year)}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function normalizeTimeInput(text: string): string | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return `${String(hours).padStart(2, "0")}:${match[2]}`;
}

/**
 * Parse a manual draft into a record value, or report why it cannot be one.
 * There is intentionally no "silently skip" result: every non-empty draft
 * either produces a value or produces an error the user can see.
 */
export function parseAndValidateFieldDraft(
  field: ValidatableField,
  draftText: string,
): ParsedFieldDraft {
  const trimmed = draftText.trim();

  if (trimmed.length === 0) {
    // An emptied input is an explicit clear, never an error.
    return {
      ok: true,
      value:
        field.kind === "list"
          ? []
          : field.path === "insurance.secondaryPayerStatus"
            ? "unknown"
            : null,
    };
  }

  switch (field.kind) {
    case "date": {
      const normalized = normalizeDateInput(trimmed);

      return normalized == null
        ? error(field.path, "Not a valid date — use MM/DD/YYYY")
        : { ok: true, value: normalized };
    }
    case "time": {
      const normalized = normalizeTimeInput(trimmed);

      return normalized == null
        ? error(field.path, "Not a valid time — use HH:MM (24h)")
        : { ok: true, value: normalized };
    }
    case "number": {
      if (!unsignedDecimalPattern.test(trimmed)) {
        return error(field.path, "Enter a number");
      }

      const parsed = Number(trimmed);

      if (!Number.isFinite(parsed)) {
        return error(field.path, "Enter a number");
      }

      if (parsed < 0) {
        return error(field.path, "Cannot be negative");
      }

      return { ok: true, value: parsed };
    }
    case "money": {
      const normalized = trimmed.replace(/[$,\s]/gu, "");

      if (normalized.length === 0) {
        return error(field.path, "Enter a dollar amount");
      }

      if (!moneyDecimalPattern.test(normalized)) {
        return error(field.path, "Use no more than 2 decimal places");
      }

      const parsed = Number(normalized);

      if (!Number.isFinite(parsed)) {
        return error(field.path, "Enter a dollar amount");
      }

      if (parsed < 0) {
        return error(field.path, "Cannot be negative");
      }

      const cents = Math.round(parsed * 100);

      if (cents > plausibilityLimits.moneyMaxCents) {
        return error(field.path, "Amount is too large");
      }

      return { ok: true, value: cents };
    }
    case "percent": {
      const normalized = trimmed.replace(/[%\s]/gu, "");

      if (normalized.length === 0) {
        return error(field.path, "Enter a percentage");
      }

      if (!unsignedDecimalPattern.test(normalized)) {
        return error(field.path, "Enter a percentage");
      }

      const parsed = Number(normalized);

      if (!Number.isFinite(parsed)) {
        return error(field.path, "Enter a percentage");
      }

      if (parsed < 0 || parsed > 100) {
        return error(field.path, "Must be between 0 and 100");
      }

      return { ok: true, value: parsed };
    }
    case "phone": {
      const normalized = normalizePhoneInput(trimmed);

      return normalized == null
        ? error(field.path, "Enter a 10-digit phone number")
        : { ok: true, value: normalized };
    }
    case "email": {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)
        ? { ok: true, value: trimmed }
        : error(field.path, "Not a valid email — check for @ and domain");
    }
    case "zip": {
      return /^\d{5}(-\d{4})?$/.test(trimmed)
        ? { ok: true, value: trimmed }
        : error(field.path, "Use a 5-digit zip (or 12345-6789)");
    }
    case "state": {
      const normalized = trimmed.toUpperCase();

      return usStateCodes.has(normalized)
        ? { ok: true, value: normalized }
        : error(field.path, "Use a 2-letter state code");
    }
    case "boolean":
      return trimmed === "true" || trimmed === "false"
        ? { ok: true, value: trimmed === "true" }
        : error(field.path, "Choose Yes or No");
    case "select":
      return field.options == null || field.options.some((option) => option.value === trimmed)
        ? { ok: true, value: trimmed }
        : error(field.path, "Choose one of the available options");
    case "list":
      return {
        ok: true,
        value: trimmed
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item.length > 0),
      };
    default:
      return { ok: true, value: trimmed };
  }
}

const plausibilityRangesByPath: Record<
  string,
  { min: number; max: number; label: string }
> = {
  "patient.heightInches": {
    min: plausibilityLimits.heightInchesMin,
    max: plausibilityLimits.heightInchesMax,
    label: "height",
  },
  "patient.weightPounds": {
    min: plausibilityLimits.weightPoundsMin,
    max: plausibilityLimits.weightPoundsMax,
    label: "weight",
  },
  "case.surgeryDurationMinutes": {
    min: plausibilityLimits.surgeryDurationMinutesMin,
    max: plausibilityLimits.surgeryDurationMinutesMax,
    label: "duration",
  },
};

/** Plausibility check for an already-valid value. Warnings never block. */
export function warningForFieldValue(
  field: ValidatableField,
  value: unknown,
): ValidationIssue | null {
  if (value == null || typeof value !== "number") {
    return null;
  }

  const range = plausibilityRangesByPath[field.path];

  if (!range) {
    return null;
  }

  if (value < range.min) {
    return issue(field.path, "warning", `Unusually low ${range.label} — double-check`);
  }

  if (value > range.max) {
    return issue(field.path, "warning", `Unusually high ${range.label} — double-check`);
  }

  return null;
}

/**
 * Path→kind registry for writers that do not have UI field descriptors
 * (scaffold transport today; real backend and questionnaire ingest later).
 * A consistency test asserts this agrees with the profile UI descriptors.
 */
export const validatedFieldKindsByPath: Record<string, FieldKind> = {
  "patient.firstName": "text",
  "patient.lastName": "text",
  "patient.mrn": "text",
  "patient.dateOfBirth": "date",
  "patient.sex": "select",
  "patient.addressStreet": "text",
  "patient.addressCity": "text",
  "patient.addressState": "state",
  "patient.addressZip": "zip",
  "patient.heightInches": "number",
  "patient.weightPounds": "number",
  "patient.primaryPhone.number": "phone",
  "patient.secondaryPhone.number": "phone",
  "patient.workPhone.number": "phone",
  "patient.homePhone.number": "phone",
  "patient.email": "email",
  "patient.primaryLanguage": "text",
  "patient.needsTranslator": "boolean",
  "patient.maritalStatus": "text",
  "patient.race": "text",
  "patient.ethnicity": "text",
  "patient.religiousAffiliation": "text",
  "patient.legalGuardian": "boolean",
  "patient.advanceDirective": "text",
  "case.dateOfService": "date",
  "case.caseTime": "time",
  "case.surgeonName": "text",
  "case.procedures": "list",
  "case.preopDiagnosis": "text",
  "case.surgeryDurationMinutes": "number",
  "case.surgeryTypeCategory": "select",
  "contacts.rideHome.name": "text",
  "contacts.rideHome.relationship": "text",
  "contacts.rideHome.phone": "phone",
  "contacts.caregiver.name": "text",
  "contacts.caregiver.relationship": "text",
  "contacts.caregiver.phone": "phone",
  "contacts.emergencyContact1.name": "text",
  "contacts.emergencyContact1.relationship": "text",
  "contacts.emergencyContact1.phone": "phone",
  "contacts.emergencyContact2.name": "text",
  "contacts.emergencyContact2.relationship": "text",
  "contacts.emergencyContact2.phone": "phone",
  "contacts.primaryCarePhysician.name": "text",
  "contacts.primaryCarePhysician.phone": "phone",
  "contacts.specialist1.name": "text",
  "contacts.specialist1.phone": "phone",
  "contacts.specialist1.specialty": "text",
  "contacts.specialist2.name": "text",
  "contacts.specialist2.phone": "phone",
  "contacts.specialist2.specialty": "text",
  "contacts.specialist3.name": "text",
  "contacts.specialist3.phone": "phone",
  "contacts.specialist3.specialty": "text",
  "contacts.mayContactPhysicians": "boolean",
  "insurance.primaryPayer.companyName": "text",
  "insurance.primaryPayer.companyPhone": "phone",
  "insurance.primaryPayer.companyAddress": "text",
  "insurance.primaryPayer.policyNumber": "text",
  "insurance.primaryPayer.groupNumber": "text",
  "insurance.primaryPayer.policyHolderName": "text",
  "insurance.primaryPayer.policyHolderRelationship": "text",
  "insurance.primaryPayer.policyHolderAddress": "text",
  "insurance.primaryPayer.policyHolderPhone": "phone",
  "insurance.primaryPayer.employerName": "text",
  "insurance.primaryPayer.employerPhone": "phone",
  "insurance.primaryPayer.employerAddress": "text",
  "insurance.secondaryPayerStatus": "select",
  "insurance.secondaryPayer.companyName": "text",
  "insurance.secondaryPayer.policyNumber": "text",
  "insurance.secondaryPayer.groupNumber": "text",
  "insurance.benefits.deductibleCents": "money",
  "insurance.benefits.deductibleMetCents": "money",
  "insurance.benefits.outOfPocketMaxCents": "money",
  "insurance.benefits.outOfPocketMetCents": "money",
  "insurance.benefits.coPaymentCents": "money",
  "insurance.benefits.patientResponsibilityCents": "money",
  "insurance.benefits.coInsurancePercent": "percent",
  ...buildClinicalFieldKinds(),
};

/**
 * Clinical questionnaire paths (Phase 1.5): derived from the contract key
 * lists and the structured-detail model so questionnaire writes (manual entry
 * and call ingest) pass the same transport validation as profile fields.
 * A test asserts every registered path exists on createEmptyPatientRecord().
 */
export const clinicalFieldKindsByPath: Record<string, FieldKind> =
  buildClinicalFieldKinds();

function buildClinicalFieldKinds(): Record<string, FieldKind> {
  const kinds: Record<string, FieldKind> = {
    "clinical.noKnownMedications": "boolean",
    "clinical.noKnownAllergies": "boolean",
    "clinical.noSurgicalHistory": "boolean",
    "clinical.neckCircumferenceOver15_75": "select",
    "contacts.preferredPharmacy.name": "text",
    "contacts.preferredPharmacy.phone": "phone",
    "clinical.structuredDetails.pacemaker.manufacturer": "text",
    "clinical.structuredDetails.cardiacStent.yearPlaced": "text",
    "clinical.structuredDetails.cardiacStent.drugEluting": "boolean",
    "clinical.structuredDetails.cardiacStent.stentType": "text",
    "clinical.structuredDetails.pulmonaryEmbolism.hasHistory": "boolean",
    "clinical.structuredDetails.openWound.present": "boolean",
    "clinical.structuredDetails.pulmonaryEmbolism.eventDate": "date",
    "clinical.structuredDetails.pulmonaryEmbolism.treatment": "text",
    "clinical.structuredDetails.pulmonaryEmbolism.recurrence": "boolean",
    "clinical.structuredDetails.pulmonaryEmbolism.onAnticoagulation": "boolean",
    "clinical.structuredDetails.homeOxygen.flowParameters": "text",
    "clinical.structuredDetails.homeOxygen.usagePattern": "select",
    "clinical.structuredDetails.homeOxygen.portableAvailable": "boolean",
    "clinical.structuredDetails.cancer.diagnosisDate": "date",
    "clinical.structuredDetails.cancer.lastTreatmentDate": "date",
    "clinical.structuredDetails.cancer.treatmentTypes": "list",
    "clinical.structuredDetails.cancer.currentStatus": "select",
    "clinical.structuredDetails.cardiacEvent.hospitalized": "boolean",
    "clinical.structuredDetails.cardiacEvent.treatment": "text",
    "clinical.structuredDetails.cardiacEvent.lastEventDate": "date",
    "clinical.structuredDetails.cardiacEvent.unresolved": "boolean",
    "clinical.structuredDetails.openWound.nearSurgicalSite": "boolean",
    "clinical.structuredDetails.openWound.woundType": "text",
    "clinical.structuredDetails.openWound.infectionSigns": "boolean",
    "clinical.structuredDetails.openWound.treatment": "text",
    "clinical.structuredDetails.currentIllness.feverPresent": "boolean",
    "clinical.structuredDetails.currentIllness.onsetDate": "date",
    "clinical.structuredDetails.currentIllness.treatment": "text",
    "clinical.structuredDetails.currentIllness.improving": "boolean",
    "clinical.structuredDetails.currentIllness.surgeonNotified": "boolean",
  };

  for (const key of clinicianWorkbenchBasicHistoryKeys) {
    kinds[`clinical.basicHistory.${key}.answer`] = "boolean";
    kinds[`clinical.basicHistory.${key}.detail`] = "text";
  }

  for (const key of clinicianWorkbenchDvtRiskFactorKeys) {
    kinds[`clinical.dvtRiskFactors.${key}`] = "boolean";
  }

  return kinds;
}

export const selectValuesByPath: Record<string, readonly string[]> = {
  "patient.sex": ["male", "female"],
  "case.surgeryTypeCategory": [
    "lower-extremity-arthroscopy",
    "lower-extremity-arthroplasty",
    "leg-fracture-surgery",
    "upper-extremity-arthroscopy",
    "upper-extremity-arthroplasty",
    "upper-extremity-fracture-surgery",
    "laparoscopy",
    "central-venous-access",
    "great-veins-of-leg",
    "other",
  ],
  "insurance.secondaryPayerStatus": ["unknown", "none", "present"],
  "clinical.neckCircumferenceOver15_75": ["yes", "no", "not_sure"],
  "clinical.structuredDetails.homeOxygen.usagePattern": [
    "continuous",
    "intermittent",
  ],
  "clinical.structuredDetails.cancer.currentStatus": [
    "in treatment",
    "remission",
    "monitored",
  ],
};

/**
 * Source-agnostic validation of a stored record value (post-parse). Used by
 * the scaffold transport; the real backend must run the same checks.
 */
export function validateRecordValue(
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
  value: unknown,
): ValidationIssue | null {
  // Catch-all questionnaire answers: any questionKey leaf, value must be a
  // canonical answer shape (string | boolean | finite number | string[]).
  if (fieldPath.startsWith("clinical.questionnaireAnswers.")) {
    const okay =
      typeof value === "string" ||
      typeof value === "boolean" ||
      (typeof value === "number" && Number.isFinite(value)) ||
      (Array.isArray(value) && value.every((item) => typeof item === "string"));

    return okay
      ? null
      : {
          fieldPath,
          severity: "error",
          message:
            "Questionnaire answers must be text, yes/no, a number, or a list of text values",
        };
  }

  const kind = validatedFieldKindsByPath[fieldPath];

  if (!kind) {
    return issue(fieldPath, "error", "Field is not supported for profile editing");
  }

  if (value == null) {
    return kind === "list" || fieldPath === "insurance.secondaryPayerStatus"
      ? issue(fieldPath, "error", "Field cannot be cleared")
      : null;
  }

  switch (kind) {
    case "date":
      return typeof value === "string" && normalizeDateInput(value) === value
        ? null
        : issue(fieldPath, "error", "Not a valid ISO date");
    case "time":
      return typeof value === "string" && normalizeTimeInput(value) === value
        ? null
        : issue(fieldPath, "error", "Not a valid 24h time");
    case "phone":
      return typeof value === "string" && normalizePhoneInput(value) === value
        ? null
        : issue(fieldPath, "error", "Not a valid phone number");
    case "email":
      return typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
        ? null
        : issue(fieldPath, "error", "Not a valid email");
    case "zip":
      return typeof value === "string" && /^\d{5}(-\d{4})?$/.test(value)
        ? null
        : issue(fieldPath, "error", "Not a valid zip code");
    case "state":
      return typeof value === "string" && usStateCodes.has(value)
        ? null
        : issue(fieldPath, "error", "Not a valid state code");
    case "number":
      return typeof value === "number" && Number.isFinite(value) && value >= 0
        ? null
        : issue(fieldPath, "error", "Not a valid number");
    case "money":
      return typeof value === "number" &&
        Number.isInteger(value) &&
        value >= 0 &&
        value <= plausibilityLimits.moneyMaxCents
        ? null
        : issue(fieldPath, "error", "Not a valid amount");
    case "percent":
      return typeof value === "number" && value >= 0 && value <= 100
        ? null
        : issue(fieldPath, "error", "Not a valid percentage");
    case "text":
      return typeof value === "string"
        ? null
        : issue(fieldPath, "error", "Not valid text");
    case "boolean":
      return typeof value === "boolean"
        ? null
        : issue(fieldPath, "error", "Not a valid Yes/No value");
    case "select":
      return typeof value === "string" &&
        (selectValuesByPath[fieldPath]?.includes(value) ?? false)
        ? null
        : issue(fieldPath, "error", "Not a valid option");
    case "list":
      return Array.isArray(value) && value.every((item) => typeof item === "string")
        ? null
        : issue(fieldPath, "error", "Not a valid list");
  }
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();

  return Math.round((to - from) / 86_400_000);
}

/**
 * Rules that relate fields to each other, evaluated on a whole (merged)
 * record. All current rules are warnings by design: unusual clinical or
 * billing reality must never hard-block a save.
 */
export function validateCrossFieldRules(
  record: ClinicianWorkbenchPatientRecordV1,
  referenceDateIso?: string | null,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const benefits = record.insurance.benefits;

  if (
    benefits.deductibleCents != null &&
    benefits.deductibleMetCents != null &&
    benefits.deductibleMetCents > benefits.deductibleCents
  ) {
    issues.push(
      issue(
        "insurance.benefits.deductibleMetCents",
        "warning",
        "Deductible met exceeds the deductible — double-check",
      ),
    );
  }

  if (
    benefits.outOfPocketMaxCents != null &&
    benefits.outOfPocketMetCents != null &&
    benefits.outOfPocketMetCents > benefits.outOfPocketMaxCents
  ) {
    issues.push(
      issue(
        "insurance.benefits.outOfPocketMetCents",
        "warning",
        "Out-of-pocket met exceeds the maximum — double-check",
      ),
    );
  }

  const dateOfBirth = record.patient.dateOfBirth;
  const dateOfService = record.case.dateOfService;

  if (dateOfBirth != null && dateOfService != null) {
    if (dateOfBirth >= dateOfService) {
      issues.push(
        issue(
          "case.dateOfService",
          "warning",
          "Date of service is on or before the date of birth — double-check",
        ),
      );
    }

    const age = computeAgeYears(dateOfBirth, dateOfService);

    if (age != null && age > plausibilityLimits.ageAtServiceMaxYears) {
      issues.push(
        issue(
          "patient.dateOfBirth",
          "warning",
          "Patient would be over 120 at the date of service — double-check",
        ),
      );
    }
  }

  if (dateOfService != null && referenceDateIso != null) {
    const offsetDays = daysBetween(referenceDateIso, dateOfService);

    if (offsetDays < -plausibilityLimits.dateOfServicePastGraceDays) {
      issues.push(
        issue(
          "case.dateOfService",
          "warning",
          "Date of service is more than 30 days in the past — double-check",
        ),
      );
    } else if (offsetDays > plausibilityLimits.dateOfServiceFutureHorizonDays) {
      issues.push(
        issue(
          "case.dateOfService",
          "warning",
          "Date of service is more than 2 years out — double-check",
        ),
      );
    }
  }

  if (
    record.insurance.secondaryPayerStatus === "present" &&
    (record.insurance.secondaryPayer.policyNumber == null ||
      record.insurance.secondaryPayer.policyNumber.trim().length === 0)
  ) {
    issues.push(
      issue(
        "insurance.secondaryPayer.policyNumber",
        "warning",
        "Secondary payer is marked present but has no policy number",
      ),
    );
  }

  return issues;
}

/** Progressive US phone formatting for assisted input: digits → (xxx) xxx-xxxx. */
export function formatPhoneDraftAsTyped(text: string): string {
  if (text.trim().startsWith("+")) {
    return text;
  }

  const rawDigits = text.replace(/\D/gu, "");
  const digits = rawDigits.length === 11 && rawDigits.startsWith("1")
    ? rawDigits.slice(1)
    : rawDigits;

  if (digits.length > 10) {
    return text;
  }

  if (digits.length === 0) {
    return "";
  }

  if (digits.length < 4) {
    return `(${digits}`;
  }

  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
